import React, {useCallback, useEffect, useMemo, useState} from 'react';
import RefreshCheckApi from '../../communication_system/axios_apis/refresh_check/refresh_check_api';
import {clearAdminSession} from '../../communication_system/axios_apis/refresh_check/refresh_check';
import PayInfoDetailModal from '../../components/SyrupComponents/PayInfoDetailModal';

const DEFAULT_PARENT_ORIGIN = 'https://main.senor.co.kr';
export const PAYMENT_DETAIL_RETURN_KEY = 'sinor_payment_detail_return';

function positiveInteger(value) {
    if (!/^\d+$/.test(value || '')) return null;
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

function configuredParentOrigins() {
    const origins = new Set([DEFAULT_PARENT_ORIGIN]);
    try {
        const configured = new URL(process.env.REACT_APP_INYEON_ORIGIN || '');
        if (['localhost', '127.0.0.1', '::1'].includes(configured.hostname)) origins.add(configured.origin);
    } catch (error) {}
    return origins;
}

function readTarget(search = window.location.search) {
    const params = new URLSearchParams(search);
    const target = {
        eventId: positiveInteger(params.get('eventId')),
        participantId: positiveInteger(params.get('participantId')),
        payId: positiveInteger(params.get('payId')),
        userId: positiveInteger(params.get('userId')),
        nonce: params.get('nonce') || '',
        parentOrigin: params.get('parentOrigin') || '',
    };
    if (!target.eventId || !target.participantId || !target.payId || !target.userId || target.nonce.length < 16 || target.nonce.length > 256) return null;
    let parentOrigin;
    try { parentOrigin = new URL(target.parentOrigin).origin; } catch (error) { return null; }
    if (parentOrigin !== target.parentOrigin || !configuredParentOrigins().has(parentOrigin)) return null;
    return {...target, parentOrigin};
}

export function validatedPaymentDetailPath(path) {
    if (!path) return '';
    try {
        const url = new URL(path, window.location.origin);
        if (url.origin !== window.location.origin || url.pathname !== '/gathering/payment-detail' || !readTarget(url.search)) return '';
        return `${url.pathname}${url.search}`;
    } catch (error) {
        return '';
    }
}

export default function PaymentDetailEmbed() {
    const target = useMemo(readTarget, []);
    const [auth, setAuth] = useState('checking');

    useEffect(() => {
        if (!target) { setAuth('invalid'); return; }
        const returnPath = validatedPaymentDetailPath(`${window.location.pathname}${window.location.search}`);
        if (returnPath) window.sessionStorage.setItem(PAYMENT_DETAIL_RETURN_KEY, returnPath);
        let current = true;
        RefreshCheckApi.get('/admin/check').then((response) => {
            if (!current) return;
            if (response?.data?.info?.id) {
                if (window.sessionStorage.getItem(PAYMENT_DETAIL_RETURN_KEY) === returnPath) {
                    window.sessionStorage.removeItem(PAYMENT_DETAIL_RETURN_KEY);
                }
                setAuth('ready');
            } else {
                setAuth('required');
            }
        }).catch(() => { if (current) setAuth('required'); });
        return () => { current = false; };
    }, [target]);

    const send = useCallback((action) => {
        if (!target) return;
        window.parent.postMessage({
            type: 'sinor-payment-detail', action,
            eventId: target.eventId, participantId: target.participantId,
            payId: target.payId, nonce: target.nonce,
        }, target.parentOrigin);
    }, [target]);

    const openLogin = useCallback(() => {
        const returnPath = validatedPaymentDetailPath(`${window.location.pathname}${window.location.search}`);
        if (returnPath) window.sessionStorage.setItem(PAYMENT_DETAIL_RETURN_KEY, returnPath);
        clearAdminSession();
    }, []);

    if (!target || auth === 'invalid') return <main role="alert">잘못되었거나 허용되지 않은 결제 상세 요청입니다.</main>;
    if (auth === 'checking') return <main>관리자 인증을 확인하고 있습니다.</main>;
    if (auth !== 'ready') return <main role="alert">관리자 로그인이 필요합니다. <button type="button" onClick={openLogin}>로그인</button></main>;

    return <PayInfoDetailModal
        payInfoDetailToggle={[true, target.payId]}
        setToggle={() => send('closed')}
        type="모임"
        adminScheduleRefund
        scheduleRefundContext={{payId: target.payId, userId: target.userId, contentsId: target.eventId}}
        embedded={{...target, onReady: () => send('ready'), onCompleted: () => send('completed'), onClose: () => send('closed')}}
    />;
}
